import { Skeleton, Stack } from '@mui/material';
import { useMemo } from 'react';
import {
  Conversation,
  useCurrentUserProfile,
  useGroupRules,
  useResolvedAddress,
  UserGroupRules,
} from 'src/graphql';
import { SidePanelPersonalizationProvider } from 'src/graphql/entities/side-panel-personalization';
import { CrossPartnerUserSettingsProvider } from 'src/graphql/entities/side-panel-personalization/cross-partner-user-settings-provider';
import { ConversationAttachments } from 'src/pages/Conversations/SidePanel/ConversationDetails/Attachments';
import { ConversationTabs } from './conversation-tabs';
import { ConversationPropertiesPanel } from './properties/conversation-properties-panel';
import { ConversationSidePanelProvider } from './properties/conversation-side-panel-provider';
import { conversationSidePanelSurface } from './properties/conversation-surface';
import { ConversationSummary } from './Summary/conversation-summary';

interface ConversationDetailProps {
  conversation: Conversation;
  onUpdate: (c: Conversation) => Promise<void>;
}

const ConversationDetailsSkeleton = () => (
  <Stack spacing={1} flexGrow={1}>
    <Skeleton width="100%" height={20} />
    {Array.from(Array(5).keys()).map((i) => (
      <Stack key={i} direction="row" gap={1}>
        <Skeleton width="37%" height={20} />
        <Skeleton width={`${Math.floor(Math.random() * 40) + 20}%`} height={20} />
      </Stack>
    ))}
  </Stack>
);

export const ConversationDetails = ({ conversation, onUpdate }: ConversationDetailProps) => {
  const { groupRules } = useGroupRules();
  const { resolvedAddress, loading } = useResolvedAddress(conversation.address);
  const { user } = useCurrentUserProfile();

  const conversationWithResolvedAddress = useMemo(
    () =>
      resolvedAddress && conversation.address !== resolvedAddress
        ? { ...conversation, address: resolvedAddress }
        : conversation,
    [conversation, resolvedAddress],
  );

  if (loading && conversation.address) {
    return <ConversationDetailsSkeleton />;
  }

  return (
    <Stack spacing={2} flexGrow={1}>
      {groupRules[UserGroupRules.AccessToAiConversationSummary] ? (
        <ConversationSummary conversation={conversationWithResolvedAddress} />
      ) : undefined}
      <CrossPartnerUserSettingsProvider>
        <ConversationSidePanelProvider
          key={`properties-${conversation.conversationId}`}
          conversation={conversationWithResolvedAddress}
          onUpdate={onUpdate}
        >
          {/* the personalization layout is shared by the properties panel and the
              site/client cards under the tabs; it needs the custom-attributes
              context, so it cannot sit higher */}
          <SidePanelPersonalizationProvider surface={conversationSidePanelSurface}>
            <ConversationPropertiesPanel />
            <ConversationAttachments
              attachments={conversation.attachments}
              conversationId={conversation.conversationId}
              siteAddressId={conversation.associatedAddresses?.[0]?.addressId}
              userId={user?.id}
            />
            <ConversationTabs
              conversation={conversationWithResolvedAddress}
              key={`tabs-${conversationWithResolvedAddress.conversationId}`}
            />
          </SidePanelPersonalizationProvider>
        </ConversationSidePanelProvider>
      </CrossPartnerUserSettingsProvider>
    </Stack>
  );
};
